import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Button, Chip, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Select, MenuItem, FormControl, InputLabel,
  Alert, CircularProgress, Card, CardContent, Grid, Divider
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import axios from 'axios';
import authService from '../../services/authService';

const API_URL = process.env.REACT_APP_API_URL || '';

interface Ticket {
  id: number;
  ticket_id: string;
  subject: string;
  description: string;
  category: string;
  status: string;
  priority: string;
  lab_id: string | null;
  admin_notes: string | null;
  created_at: string | null;
  updated_at: string | null;
  resolved_at: string | null;
}

const statusColor = (s: string) => {
  switch (s) {
    case 'open': return 'error';
    case 'in_progress': return 'warning';
    case 'resolved': return 'success';
    case 'closed': return 'default';
    default: return 'default';
  }
};

const priorityColor = (p: string) => {
  switch (p) {
    case 'urgent': return 'error';
    case 'high': return 'warning';
    case 'normal': return 'primary';
    case 'low': return 'default';
    default: return 'default';
  }
};

const categoryLabel = (c: string) => {
  switch (c) {
    case 'integration_removal': return 'Integration Removal';
    case 'feature_request': return 'Feature Request';
    case 'bug': return 'Bug Report';
    case 'general': return 'General';
    default: return c;
  }
};

const SupportTickets: React.FC = () => {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [ticketForm, setTicketForm] = useState({
    subject: '', description: '', category: 'general', priority: 'normal', lab_id: '',
  });

  // Detail dialog
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const getHeaders = () => authService.getAuthHeaders();

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/v1/lis-portal/support-tickets`, { headers: getHeaders() });
      if (response.data.success) {
        setTickets(response.data.tickets || []);
      }
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch tickets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTickets(); }, []);

  const handleCreateTicket = async () => {
    try {
      const payload: any = {
        subject: ticketForm.subject,
        description: ticketForm.description,
        category: ticketForm.category,
        priority: ticketForm.priority,
      };
      if (ticketForm.lab_id) payload.lab_id = ticketForm.lab_id;

      const response = await axios.post(
        `${API_URL}/api/v1/lis-portal/support-tickets`,
        payload,
        { headers: getHeaders() }
      );
      if (response.data.success) {
        setCreateOpen(false);
        setSuccess(response.data.message);
        setTicketForm({ subject: '', description: '', category: 'general', priority: 'normal', lab_id: '' });
        fetchTickets();
      } else {
        setError(response.data.error || 'Failed to create ticket');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create ticket');
    }
  };

  const openDetail = (ticket: Ticket) => {
    setSelectedTicket(ticket);
    setDetailOpen(true);
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Support Tickets</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button startIcon={<RefreshIcon />} onClick={fetchTickets}>Refresh</Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
            New Ticket
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* Summary */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Card><CardContent sx={{ textAlign: 'center', py: 1 }}>
            <Typography variant="h5">{tickets.length}</Typography>
            <Typography variant="caption" color="text.secondary">Total</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card><CardContent sx={{ textAlign: 'center', py: 1 }}>
            <Typography variant="h5" color="error.main">{tickets.filter(t => t.status === 'open').length}</Typography>
            <Typography variant="caption" color="text.secondary">Open</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card><CardContent sx={{ textAlign: 'center', py: 1 }}>
            <Typography variant="h5" color="warning.main">{tickets.filter(t => t.status === 'in_progress').length}</Typography>
            <Typography variant="caption" color="text.secondary">In Progress</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card><CardContent sx={{ textAlign: 'center', py: 1 }}>
            <Typography variant="h5" color="success.main">{tickets.filter(t => t.status === 'resolved' || t.status === 'closed').length}</Typography>
            <Typography variant="caption" color="text.secondary">Resolved</Typography>
          </CardContent></Card>
        </Grid>
      </Grid>

      {tickets.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>No tickets yet</Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Need help? Submit a support ticket and our team will respond promptly.
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
            Create Your First Ticket
          </Button>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Ticket ID</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell>Category</TableCell>
                <TableCell align="center">Priority</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell>Created</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tickets.map((t) => (
                <TableRow key={t.id} hover sx={{ cursor: 'pointer' }} onClick={() => openDetail(t)}>
                  <TableCell><strong>{t.ticket_id}</strong></TableCell>
                  <TableCell>{t.subject}</TableCell>
                  <TableCell>{categoryLabel(t.category)}</TableCell>
                  <TableCell align="center">
                    <Chip label={t.priority} size="small" color={priorityColor(t.priority) as any} />
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={t.status.replace('_', ' ')} size="small" color={statusColor(t.status) as any} />
                  </TableCell>
                  <TableCell>{t.created_at ? new Date(t.created_at).toLocaleDateString() : '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create Ticket Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Support Ticket</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Describe your issue and our team will get back to you promptly.
          </Typography>
          <TextField label="Subject" fullWidth margin="normal" required
            placeholder="Brief summary of your issue"
            value={ticketForm.subject} onChange={(e) => setTicketForm({ ...ticketForm, subject: e.target.value })} />
          <FormControl fullWidth margin="normal">
            <InputLabel>Category</InputLabel>
            <Select value={ticketForm.category} label="Category"
              onChange={(e) => setTicketForm({ ...ticketForm, category: e.target.value })}>
              <MenuItem value="general">General</MenuItem>
              <MenuItem value="integration_removal">Integration Removal</MenuItem>
              <MenuItem value="bug">Bug Report</MenuItem>
              <MenuItem value="feature_request">Feature Request</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Priority</InputLabel>
            <Select value={ticketForm.priority} label="Priority"
              onChange={(e) => setTicketForm({ ...ticketForm, priority: e.target.value })}>
              <MenuItem value="low">Low</MenuItem>
              <MenuItem value="normal">Normal</MenuItem>
              <MenuItem value="high">High</MenuItem>
              <MenuItem value="urgent">Urgent</MenuItem>
            </Select>
          </FormControl>
          <TextField label="Lab ID (optional)" fullWidth margin="normal"
            placeholder="Related lab ID if applicable"
            value={ticketForm.lab_id} onChange={(e) => setTicketForm({ ...ticketForm, lab_id: e.target.value })} />
          <TextField label="Description" fullWidth margin="normal" required multiline rows={4}
            placeholder="Provide details about your issue, including any relevant integration or lab information..."
            value={ticketForm.description} onChange={(e) => setTicketForm({ ...ticketForm, description: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateTicket}
            disabled={!ticketForm.subject || !ticketForm.description}>
            Submit Ticket
          </Button>
        </DialogActions>
      </Dialog>

      {/* Ticket Detail Dialog */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="sm" fullWidth>
        {selectedTicket && (
          <>
            <DialogTitle>{selectedTicket.ticket_id} - {selectedTicket.subject}</DialogTitle>
            <DialogContent>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <Typography variant="body2"><strong>Category:</strong> {categoryLabel(selectedTicket.category)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2"><strong>Priority:</strong> <Chip label={selectedTicket.priority} size="small" color={priorityColor(selectedTicket.priority) as any} /></Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2"><strong>Status:</strong> <Chip label={selectedTicket.status.replace('_', ' ')} size="small" color={statusColor(selectedTicket.status) as any} /></Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2"><strong>Lab ID:</strong> {selectedTicket.lab_id || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2"><strong>Created:</strong> {selectedTicket.created_at ? new Date(selectedTicket.created_at).toLocaleString() : '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2"><strong>Resolved:</strong> {selectedTicket.resolved_at ? new Date(selectedTicket.resolved_at).toLocaleString() : '-'}</Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" sx={{ mb: 1 }}>Description</Typography>
              <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{selectedTicket.description}</Typography>
              </Paper>

              {selectedTicket.admin_notes && (
                <>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Response from ReasonShield</Typography>
                  <Paper sx={{ p: 2, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.light' }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{selectedTicket.admin_notes}</Typography>
                  </Paper>
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetailOpen(false)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default SupportTickets;
